import { Suspense } from "react";
import { ThemeProvider } from "@mui/material/styles";
import { CssBaseline } from "@mui/material";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { isDevFeaturesEnabled } from "./utils/environment";
import theme from "./theme";
import { AuthProvider } from "./contexts/AuthContext";
import { TenantProvider } from "./contexts/TenantContext";
import { QueryProvider } from "./providers/QueryProvider";
import { PageLoadingFallback } from "./components/common/LazyLoadWrapper";
import { AppErrorBoundary } from "./components/common/AppErrorBoundary";
import { RootErrorBoundary } from "./components/common/RootErrorBoundary";
import { lazyWithRetry } from "./utils/lazyWithRetry";

// Core components - loaded immediately (small, always needed)
import Login from "./components/auth/Login";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import MainLayout from "./components/layout/MainLayout";
import EnvironmentBadge from "./components/common/EnvironmentBadge";

// Lazy-loaded components - loaded on demand (lazyWithRetry auto-reloads on stale chunks)
const CaseManagementDashboard = lazyWithRetry(
  () => import("./components/dashboard/CaseManagementDashboard"),
);
const CasesPage = lazyWithRetry(() => import("./pages/CasesPage"));
const CaseDetailPage = lazyWithRetry(() => import("./pages/CaseDetailPage"));
const TestPage = lazyWithRetry(() => import("./pages/TestPage"));
const NeedsAssessmentPage = lazyWithRetry(
  () => import("./pages/NeedsAssessmentPage"),
);
const NeedsAssessmentDownloadPage = lazyWithRetry(
  () => import("./pages/NeedsAssessmentDownloadPage"),
);
const NeedsAssessmentTemplateListPage = lazyWithRetry(
  () => import("./pages/NeedsAssessmentTemplateListPage"),
);
const NeedsAssessmentTemplateEditPage = lazyWithRetry(
  () => import("./pages/NeedsAssessmentTemplateEditPage"),
);
const NeedsAssessmentTemplateCreatePage = lazyWithRetry(
  () => import("./pages/NeedsAssessmentTemplateCreatePage"),
);
const NoAccessPage = lazyWithRetry(() => import("./pages/NoAccessPage"));
const NotFoundPage = lazyWithRetry(() => import("./pages/NotFoundPage"));
const OnboardingQuestionnairePage = lazyWithRetry(
  () => import("./pages/OnboardingQuestionnairePage"),
);
const CompletedQuestionnairePage = lazyWithRetry(
  () => import("./pages/CompletedQuestionnairePage"),
);

// Healthcare Pages - Lazy loaded
const HealthcareUploadPage = lazyWithRetry(
  () => import("./pages/healthcare/UploadPage"),
);
const HealthcareFilesPage = lazyWithRetry(
  () => import("./pages/healthcare/DocumentLibraryPage"),
);
const HealthcareProcessingPage = lazyWithRetry(
  () => import("./pages/healthcare/ProcessingQueuePage"),
);
const HealthcareRulesPage = lazyWithRetry(
  () => import("./pages/healthcare/ComplianceRulesPage"),
);
const AdminDocumentTypesPage = lazyWithRetry(
  () => import("./pages/healthcare/DocumentTypesPage"),
);
const AdminExtractionPromptsPage = lazyWithRetry(
  () => import("./pages/healthcare/ExtractionPromptsPage"),
);
const RuleDefaultAssignmentsPage = lazyWithRetry(
  () => import("./pages/healthcare/RuleDefaultAssignmentsPage"),
);
const JobDetailsPage = lazyWithRetry(() =>
  import("./pages/healthcare/JobDetailsPage").then((m) => ({
    default: m.JobDetailsPage,
  })),
);

// Subscription Pages - Lazy loaded (includes Stripe)
const SubscriptionPlansPage = lazyWithRetry(
  () => import("./pages/subscriptions/SubscriptionPlansPage"),
);
const PurchaseFlowPage = lazyWithRetry(
  () => import("./pages/subscriptions/PurchaseFlowPage"),
);
const PaymentSuccessPage = lazyWithRetry(
  () => import("./pages/subscriptions/PaymentSuccessPage"),
);
const PaymentCancelPage = lazyWithRetry(
  () => import("./pages/subscriptions/PaymentCancelPage"),
);
const SubscriptionManagementPage = lazyWithRetry(
  () => import("./pages/subscriptions/SubscriptionManagementPage"),
);
const CreatePackagePage = lazyWithRetry(
  () => import("./pages/subscriptions/CreatePackagePage"),
);
const SubscriptionReviewPage = lazyWithRetry(
  () => import("./pages/subscriptions/SubscriptionReviewPage"),
);

// User Management Pages - Lazy loaded
const UserListPage = lazyWithRetry(
  () => import("./features/user-management/pages/UserListPage"),
);
const UserProfilePage = lazyWithRetry(
  () => import("./features/user-management/pages/UserProfilePage"),
);
const CreateUserPage = lazyWithRetry(
  () => import("./features/user-management/pages/CreateUserPage"),
);
const EditUserPage = lazyWithRetry(
  () => import("./features/user-management/pages/EditUserPage"),
);
const RoleListPage = lazyWithRetry(
  () => import("./features/user-management/pages/RoleListPage"),
);

// Tenant Management Pages - Lazy loaded
const TenantListPage = lazyWithRetry(
  () => import("./features/tenant-management/pages/TenantListPage"),
);
const TenantDetailPage = lazyWithRetry(
  () => import("./features/tenant-management/pages/TenantDetailPage"),
);
const CreateTenantPage = lazyWithRetry(
  () => import("./features/tenant-management/pages/CreateTenantPage"),
);
const EditTenantPage = lazyWithRetry(
  () => import("./features/tenant-management/pages/EditTenantPage"),
);

// Auth Pages - Lazy loaded
const ForgotPasswordPage = lazyWithRetry(
  () => import("./pages/ForgotPasswordPage"),
);
const ResetPasswordPage = lazyWithRetry(
  () => import("./pages/ResetPasswordPage"),
);

// Audit Logs Page - Lazy loaded
const AuditLogsPage = lazyWithRetry(() => import("./pages/AuditLogsPage"));
const AccountSecurityPage = lazyWithRetry(
  () => import("./pages/AccountSecurityPage"),
);

// Questionnaire Management Pages - Lazy loaded
const QuestionnairesListPage = lazyWithRetry(
  () => import("./pages/QuestionnairesListPage"),
);
const QuestionnaireManagementPage = lazyWithRetry(
  () => import("./pages/QuestionnaireManagementPage"),
);
const QuestionnaireDetailPage = lazyWithRetry(
  () => import("./pages/QuestionnaireDetailPage"),
);

// Chat Window - Lazy loaded (includes markdown libs)
const ChatWindow = lazyWithRetry(() =>
  import("./components/chat").then((m) => ({ default: m.ChatWindow })),
);

// Onboarding Guard - Keep eager as it's used frequently
import OnboardingGuard from "./components/auth/OnboardingGuard";

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <RootErrorBoundary>
        <QueryProvider>
          <AuthProvider>
            <TenantProvider>
              <AppErrorBoundary>
                <Router>
                  <Suspense fallback={<PageLoadingFallback />}>
                    <Routes>
                      {/* Public routes */}
                      <Route path="/login" element={<Login />} />
                      <Route
                        path="/forgot-password"
                        element={<ForgotPasswordPage />}
                      />
                      <Route
                        path="/reset-password"
                        element={<ResetPasswordPage />}
                      />

                      {/* No Access route - for users without entitlements */}
                      <Route
                        path="/no-access"
                        element={
                          <ProtectedRoute>
                            <NoAccessPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Protected routes */}
                      <Route
                        path="/*"
                        element={
                          <ProtectedRoute>
                            <MainLayout>
                              <Suspense fallback={<PageLoadingFallback />}>
                                <Routes>
                                  <Route
                                    path="/"
                                    element={<CaseManagementDashboard />}
                                  />
                                  <Route
                                    path="/cases"
                                    element={
                                      <Navigate to="/cases/open" replace />
                                    }
                                  />
                                  <Route
                                    path="/cases/open"
                                    element={<CasesPage mode="open" />}
                                  />
                                  <Route
                                    path="/cases/closed"
                                    element={<CasesPage mode="closed" />}
                                  />
                                  <Route
                                    path="/cases/:id"
                                    element={<CaseDetailPage />}
                                  />
                                  <Route
                                    path="/needs-assessment/templates"
                                    element={
                                      <NeedsAssessmentTemplateListPage />
                                    }
                                  />
                                  <Route
                                    path="/needs-assessment/templates/create"
                                    element={
                                      <NeedsAssessmentTemplateCreatePage />
                                    }
                                  />
                                  <Route
                                    path="/needs-assessment/templates/:id/edit"
                                    element={
                                      <NeedsAssessmentTemplateEditPage />
                                    }
                                  />
                                  <Route
                                    path="/needs-assessment/template"
                                    element={<NeedsAssessmentPage />}
                                  />
                                  <Route
                                    path="/needs-assessment/download"
                                    element={<NeedsAssessmentDownloadPage />}
                                  />

                                  {/* Onboarding Route - No guard needed */}
                                  <Route
                                    path="/onboarding"
                                    element={<OnboardingQuestionnairePage />}
                                  />

                                  {/* Compliance Management Routes */}
                                  <Route
                                    path="/compliance/questionnaire"
                                    element={<CompletedQuestionnairePage />}
                                  />

                                  {/* Healthcare Compliance Routes - Document Upload requires onboarding */}
                                  <Route
                                    path="/healthcare/upload"
                                    element={
                                      <OnboardingGuard>
                                        <HealthcareUploadPage />
                                      </OnboardingGuard>
                                    }
                                  />
                                  <Route
                                    path="/healthcare/files"
                                    element={<HealthcareFilesPage />}
                                  />
                                  <Route
                                    path="/healthcare/processing"
                                    element={<HealthcareProcessingPage />}
                                  />
                                  <Route
                                    path="/healthcare/extraction/:jobId"
                                    element={<JobDetailsPage />}
                                  />
                                  <Route
                                    path="/healthcare/rules"
                                    element={<HealthcareRulesPage />}
                                  />
                                  <Route
                                    path="/healthcare/rule-assignments"
                                    element={<RuleDefaultAssignmentsPage />}
                                  />

                                  {/* Subscription Routes */}
                                  <Route
                                    path="/subscriptions/plans"
                                    element={<SubscriptionPlansPage />}
                                  />
                                  <Route
                                    path="/subscriptions/create"
                                    element={<CreatePackagePage />}
                                  />
                                  <Route
                                    path="/subscriptions/purchase/:planId"
                                    element={<PurchaseFlowPage />}
                                  />
                                  <Route
                                    path="/subscriptions/payment/success"
                                    element={<PaymentSuccessPage />}
                                  />
                                  <Route
                                    path="/subscriptions/payment/cancel"
                                    element={<PaymentCancelPage />}
                                  />
                                  <Route
                                    path="/subscriptions/manage"
                                    element={<SubscriptionManagementPage />}
                                  />
                                  <Route
                                    path="/subscriptions/review"
                                    element={<SubscriptionReviewPage />}
                                  />

                                  {/* Admin Routes */}
                                  <Route
                                    path="/admin/document-types"
                                    element={<AdminDocumentTypesPage />}
                                  />
                                  <Route
                                    path="/admin/prompts"
                                    element={<AdminExtractionPromptsPage />}
                                  />

                                  {/* Tenant Management Routes */}
                                  <Route
                                    path="/admin/tenants"
                                    element={<TenantListPage />}
                                  />
                                  <Route
                                    path="/admin/tenants/new"
                                    element={<CreateTenantPage />}
                                  />
                                  <Route
                                    path="/admin/tenants/:tenantId"
                                    element={<TenantDetailPage />}
                                  />
                                  <Route
                                    path="/admin/tenants/:tenantId/edit"
                                    element={<EditTenantPage />}
                                  />

                                  {/* User Management Routes */}
                                  <Route
                                    path="/users"
                                    element={<UserListPage />}
                                  />
                                  <Route
                                    path="/users/new"
                                    element={<CreateUserPage />}
                                  />
                                  <Route
                                    path="/users/:userId"
                                    element={<UserProfilePage />}
                                  />
                                  <Route
                                    path="/users/:userId/edit"
                                    element={<EditUserPage />}
                                  />
                                  <Route
                                    path="/roles"
                                    element={<RoleListPage />}
                                  />
                                  <Route
                                    path="/questionnaires"
                                    element={<QuestionnairesListPage />}
                                  />
                                  <Route
                                    path="/questionnaires/new"
                                    element={<QuestionnaireManagementPage />}
                                  />
                                  <Route
                                    path="/questionnaires/:id"
                                    element={<QuestionnaireDetailPage />}
                                  />
                                  {/* Audit Logs Route */}
                                  <Route
                                    path="/audit-logs"
                                    element={<AuditLogsPage />}
                                  />

                                  {/* Account Security / MFA */}
                                  <Route
                                    path="/account/security"
                                    element={<AccountSecurityPage />}
                                  />

                                  {/* API Test route - only available in development */}
                                  {isDevFeaturesEnabled() && (
                                    <Route
                                      path="/test"
                                      element={<TestPage />}
                                    />
                                  )}

                                  {/* 404 catch-all — must be last */}
                                  <Route path="*" element={<NotFoundPage />} />
                                </Routes>
                              </Suspense>
                            </MainLayout>
                          </ProtectedRoute>
                        }
                      />
                    </Routes>
                  </Suspense>
                </Router>

                {/* Integrated Chat Window */}
                <Suspense fallback={null}>
                  <ChatWindow />
                </Suspense>
              </AppErrorBoundary>
            </TenantProvider>
          </AuthProvider>
        </QueryProvider>
      </RootErrorBoundary>

      {/* Global Toast Notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: "#363636",
            color: "#fff",
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: "#10B981",
              secondary: "#fff",
            },
          },
          error: {
            duration: 4000,
            iconTheme: {
              primary: "#EF4444",
              secondary: "#fff",
            },
          },
        }}
      />

      <EnvironmentBadge />
    </ThemeProvider>
  );
}

export default App;
